import { useState, useEffect } from "react";
import {
  ChevronDown, Lock, FileBarChart,
} from "lucide-react";

/**
 * Dropdown "Relatórios" da barra de navegação do cliente.
 * Estrutura cresce VERTICALMENTE (acordeon inline) para evitar overflow horizontal.
 *
 *   Relatórios ▾
 *    ├ ... (itens com path/action)
 *    ├ Admin ▾
 *    │   ├ Usuários Fixos
 *    │   ├ Autorizações
 *    │   ├ Setores
 *    │   ├ Horários
 *    │   └ Grupos de Acesso
 *    └ Auditoria ▾
 *        ├ Cadastros ▾ (3º nível)
 *        └ ...
 *
 * Cada item com `children` abre/fecha de forma INDEPENDENTE (estado por label),
 * para permitir vários submenus (Admin, Auditoria, ...) no mesmo dropdown.
 */
export default function NavRelatoriosMenu({
  open,
  onMouseEnter,
  onMouseLeave,
  onToggle,
  onClose,
  navigate,
  isActive,
  isInPath,
  hasRelatoriosPermission,
  relatorioItems,
  onOpenAuditoriaModal,
  onOpenRelAutorizacoes,
  onOpenPerfilVisitante,
}) {
  // Submenus abertos por label (2º e 3º nível), independentes entre si.
  const [openParents, setOpenParents] = useState({});
  const [openSubParents, setOpenSubParents] = useState({});

  // Zera os acordeões quando o dropdown principal fecha
  useEffect(() => {
    if (!open) {
      setOpenParents({});
      setOpenSubParents({});
    }
  }, [open]);

  const toggleParent = (label) => setOpenParents((p) => ({ ...p, [label]: !p[label] }));
  const toggleSubParent = (label) => setOpenSubParents((p) => ({ ...p, [label]: !p[label] }));
  const closeAll = () => { setOpenParents({}); setOpenSubParents({}); onClose(); };

  // Ação de um item "folha" (path / action / modulo).
  const runLeaf = (leaf) => {
    if (leaf.action === "autorizacoes_por_autorizador") {
      onOpenRelAutorizacoes?.();
    } else if (leaf.action === "perfil_visitante") {
      onOpenPerfilVisitante?.();
    } else if (leaf.modulo) {
      onOpenAuditoriaModal?.(leaf.modulo);
    } else if (leaf.path) {
      navigate(leaf.path);
    }
    closeAll();
  };

  const leafKey = (leaf) => leaf.modulo || leaf.action || (leaf.path ? leaf.path.split("/").pop().split("?")[0] : leaf.label);

  return (
    <div
      className="relative nav-dropdown-container"
      onMouseEnter={hasRelatoriosPermission ? onMouseEnter : undefined}
      onMouseLeave={onMouseLeave}
    >
      <button
        data-testid="cliente-nav-relatorios"
        onClick={() => hasRelatoriosPermission && onToggle()}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
          !hasRelatoriosPermission ? "text-brand-ice/70 cursor-not-allowed" :
          isInPath("/cliente/relatorios")
            ? "bg-white/15 text-white"
            : "text-brand-ice hover:bg-white/10 hover:text-white"
        }`}
      >
        {hasRelatoriosPermission ? <FileBarChart className="w-4 h-4" /> : <Lock className="w-4 h-4 text-yellow-500" />}
        Relatórios
        {hasRelatoriosPermission && <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && hasRelatoriosPermission && (
        <div
          data-testid="cliente-relatorios-dropdown"
          className="absolute top-full left-0 mt-0 w-64 bg-brand-dark/95 backdrop-blur-md border border-brand-medium/30 rounded-lg shadow-xl overflow-hidden max-h-[70vh] overflow-y-auto"
        >
          {relatorioItems.map((item) => {
            if (item.children) {
              const parentSlug = item.label.toLowerCase();
              const parentOpen = !!openParents[item.label];
              return (
                <div key={item.label}>
                  <button
                    data-testid={`cliente-nav-rel-${parentSlug}`}
                    onClick={() => toggleParent(item.label)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 text-brand-ice hover:bg-white/10 hover:text-white"
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${parentOpen ? "rotate-180" : ""}`} />
                  </button>
                  {parentOpen && (
                    <div data-testid={`cliente-submenu-${parentSlug}`} className="bg-black/20 border-t border-brand-medium/20">
                      {item.children.map((sub) => {
                        // 3º nível (ex: Cadastros dentro de Auditoria)
                        if (sub.children) {
                          const subSlug = sub.label.toLowerCase();
                          const subOpen = !!openSubParents[sub.label];
                          return (
                            <div key={sub.label}>
                              <button
                                data-testid={`cliente-nav-rel-${parentSlug}-${subSlug}`}
                                onClick={() => toggleSubParent(sub.label)}
                                className="w-full flex items-center gap-3 pl-8 pr-4 py-2 text-sm transition-colors duration-150 text-brand-ice hover:bg-white/10 hover:text-white"
                              >
                                <sub.icon className="w-4 h-4" />
                                <span className="flex-1 text-left">{sub.label}</span>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${subOpen ? "rotate-180" : ""}`} />
                              </button>
                              {subOpen && (
                                <div data-testid={`cliente-submenu-${parentSlug}-${subSlug}`} className="bg-black/30 border-t border-brand-medium/15">
                                  {sub.children.map((leaf) => (
                                    <button
                                      key={leafKey(leaf)}
                                      data-testid={`cliente-nav-rel-${parentSlug}-${subSlug}-${leafKey(leaf)}`}
                                      onClick={() => runLeaf(leaf)}
                                      className="w-full flex items-center gap-3 pl-12 pr-4 py-2 text-sm transition-colors duration-150 text-brand-ice hover:bg-white/10 hover:text-white"
                                    >
                                      <leaf.icon className="w-4 h-4" />
                                      {leaf.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        // Folha (path / action / modulo)
                        return (
                          <button
                            key={leafKey(sub)}
                            data-testid={`cliente-nav-rel-${parentSlug}-${leafKey(sub)}`}
                            onClick={() => runLeaf(sub)}
                            className="w-full flex items-center gap-3 pl-8 pr-4 py-2 text-sm transition-colors duration-150 text-brand-ice hover:bg-white/10 hover:text-white"
                          >
                            <sub.icon className="w-4 h-4" />
                            {sub.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            // Item de 1º nível com path/action
            return (
              <button
                key={item.path || item.action}
                data-testid={`cliente-nav-rel-${item.action || item.path.split("/").pop()}`}
                onClick={() => {
                  if (item.action === "autorizacoes_por_autorizador") {
                    onOpenRelAutorizacoes?.();
                  } else if (item.path) {
                    navigate(item.path);
                  }
                  onClose();
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                  item.path && isActive(item.path) ? "bg-white/15 text-white" : "text-brand-ice hover:bg-white/10 hover:text-white"
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
